/* eslint-disable react-hooks/exhaustive-deps */
import { Grid, IconButton, makeStyles, TextField } from '@material-ui/core';
import SearchIcon from '@material-ui/icons/Search';
import { ParsableDate } from '@material-ui/pickers/constants/prop-types';
import DatePicker from 'components/DatePicker';
import TableItems, { TableCell, TableRow } from 'components/TableItems';
import { format } from 'date-fns';
import { Formik } from 'formik';
import i18n from 'i18n';
import { getAllClientLogsRequest } from 'modules/clients/actions';
import {
  ClientsLogsFields,
  ListClientsLogsDataProps,
} from 'modules/clients/interfaces';
import React from 'react';
import { connect } from 'react-redux';
import { useParams } from 'react-router';
import ApplicationState from 'store/interfaces';
import { useDebounce } from 'hooks';
import { cyrb53Hash } from 'utils/helpers';
import { useTableFilters } from 'hooks/useTableFilters';
import ClientsTemplate from '../Templates/Clients';
import styles from './styles';

const useStyles = makeStyles(styles);

const initial = {
  startDate: null,
  endDate: null,
};

const initialQuery = {
  offset: 0,
  limit: 25,
  search: '',
  'start-date': '',
  'end-date': '',
};

interface Props {
  getAllClientLogsRequest: ({ id, query }) => void;
  isLoading: boolean;
  clientLogs: ListClientsLogsDataProps;
}

const LogsClients = ({
  getAllClientLogsRequest,
  clientLogs,
  isLoading,
}: Props) => {
  const classes = useStyles();

  const {
    query,
    handleLimitChange,
    handlePageChange,
    handleSearch,
    setSearchValue,
    setQuery,
  } = useTableFilters(initialQuery);
  const [minDate, setMinDate] = React.useState<ParsableDate>();
  const { id } = useParams<{ id: string }>();

  const handleSubmit = (values: { startDate; endDate }) => {
    setQuery(prev => ({
      ...prev,
      'start-date': values.startDate
        ? format(values.startDate, 'yyyy-MM-dd')
        : '',
      'end-date': values.endDate ? format(values.endDate, 'yyyy-MM-dd') : '',
    }));
  };

  const debounceSearch = useDebounce({
    value: query,
  });

  React.useEffect(() => {
    if (query.search === '') {
      getAllClientLogsRequest({ id, query });
    }
  }, [getAllClientLogsRequest, id, query]);

  React.useEffect(() => {
    if (query.search !== '') {
      getAllClientLogsRequest({ id, query });
    }
  }, [debounceSearch]);

  const headers = [
    {
      centerTitle: true,
      label: i18n.t('DATE_KEY'),
    },
    {
      centerTitle: true,
      label: `${i18n.t('TIME_KEY')}s`,
    },
    {
      centerTitle: true,
      label: i18n.t('TYPE_KEY'),
    },
    {
      centerTitle: false,
      label: i18n.t('MESSAGE_KEY'),
    },
  ];

  const [isSearchLocked, setIsSearchLocked] = React.useState(false);

  const initiateSearch = () => {
    if (isSearchLocked || isLoading) return;
    setIsSearchLocked(true);

    handleSearch();

    setTimeout(() => {
      setIsSearchLocked(false);
    }, 150);
  };

  return (
    <ClientsTemplate
      current="logs"
      title={i18n.t('CLIENTS_PAGE.LIST_LOGS_TITLE_KEY')}
      subtitle={i18n.t('CLIENTS_PAGE.LIST_LOGS_SUBTITLE_KEY')}
      breadcrumbs={[
        {
          name: i18n.t('CLIENT_KEY'),
          link: '/clients',
        },
        {
          name: i18n.t('LOGS_KEY'),
        },
      ]}
    >
      <Grid container spacing={2} className={classes.container}>
        <Formik
          initialValues={initial}
          onSubmit={handleSubmit}
          enableReinitialize
        >
          {({ setFieldValue, values, submitForm }) => (
            <>
              <Grid container direction="row" spacing={2}>
                <Grid item xs={3}>
                  <TextField
                    variant="outlined"
                    fullWidth
                    className={classes.searchInput}
                    placeholder={i18n.t('SEARCH_KEY')}
                    onChange={({ target }) => {
                      setSearchValue(target.value);
                    }}
                    onKeyPress={e => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        initiateSearch();
                      }
                    }}
                    InputProps={{
                      startAdornment: (
                        <SearchIcon className={classes.searchIcon} />
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={3}>
                  <DatePicker
                    id="startDate"
                    type="date"
                    keyValue="startDate"
                    setFieldsValue={(key, value) => {
                      setFieldValue(key, value);
                      setMinDate(value);
                    }}
                    label={i18n.t('START_DATE_KEY')}
                    value={values.startDate}
                  />
                </Grid>
                <Grid item xs={3}>
                  <DatePicker
                    id="endDate"
                    type="date"
                    keyValue="endDate"
                    setFieldsValue={setFieldValue}
                    minDate={minDate}
                    helperText={i18n.t('DATE_GREATER_THAN_START_DATE_KEY')}
                    label={i18n.t('END_DATE_KEY')}
                    value={values.endDate}
                  />
                </Grid>
                <Grid item xs={2}>
                  <IconButton
                    style={{ marginTop: 5 }}
                    onClick={submitForm}
                    disabled={isLoading}
                  >
                    <SearchIcon />
                  </IconButton>
                </Grid>
              </Grid>
            </>
          )}
        </Formik>
        <Grid container className={classes.table}>
          <TableItems
            header={
              <>
                {headers?.map(header => (
                  <TableCell
                    centerTitle={header?.centerTitle}
                    key={cyrb53Hash(String(header?.label))}
                  >
                    {header?.label}
                  </TableCell>
                ))}
              </>
            }
            renderItems={(item: ClientsLogsFields, key) => (
              <TableRow key={key}>
                <TableCell className={classes.centerDate}>
                  <Grid container justify="center">
                    {item?.date}
                  </Grid>
                </TableCell>
                <TableCell className={classes.centerTimeAndTime}>
                  <Grid container justify="center">
                    {item?.time}
                  </Grid>
                </TableCell>
                <TableCell className={classes.centerTimeAndTime}>
                  <Grid container justify="center">
                    {item?.type}
                  </Grid>
                </TableCell>
                <TableCell className={classes.tableMessage}>
                  {item?.message}
                </TableCell>
              </TableRow>
            )}
            colCount={5}
            isLoading={isLoading}
            data={clientLogs}
            onChangeLimit={handleLimitChange}
            onChangePage={handlePageChange}
            notFoundText={i18n.t('CLIENTS_PAGE.NO_LOGS_KEY')}
          />
        </Grid>
      </Grid>
    </ClientsTemplate>
  );
};

const mapStateToProps = ({
  clients: { isLoading, clientLogs },
}: ApplicationState) => ({
  isLoading,
  clientLogs,
});

export default connect(mapStateToProps, {
  getAllClientLogsRequest,
})(LogsClients);
